"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { SmartImage } from '../elements/smartimage';
import { usePixelatedConfig } from '../config/config.client';
import { DragHandler } from './carousel.drag';
import './carousel.css';
/*
TODO: #20 Carousel bug conflict with drag and click
*/
/* ========== CAROUSEL IMAGE ORIENTATION ========== */
/* https://stackoverflow.com/questions/43430498/detecting-orientation-of-images-with-javascript */
function capitalize(str) {
    return str && String(str[0]).toUpperCase() + String(str).slice(1);
}
export const carouselVariants = ['stack', 'continuous'];
/* ========== CAROUSEL ========== */
/**
 * Carousel — renders a rotating set of card slides (images and optional text) with autoplay and optional drag support.
 *
 * @param {arrayOf} [props.cards] - Array of cards to display; each card may include image, header, subHeader, body and link.
 * @param {number} [props.index] - Initial active index for the carousel (zero-based).
 * @param {number} [props.cardIndex] - Current active index managed by the component (zero-based).
 * @param {number} [props.cardLength] - Total number of cards (used for stacking and calculations).
 * @param {string} [props.link] - Optional fallback link for a card when provided at the card level.
 * @param {string} [props.linkTarget] - Target attribute for links (e.g. '_self', '_blank').
 * @param {string} [props.image] - Image URL for a card.
 * @param {string} [props.imageAlt] - Alt text for the card image (accessibility).
 * @param {oneOf} [props.imgFit] - How to fit images: 'contain' | 'cover' | 'fill'.
 * @param {string} [props.headerText] - Optional card header/title text.
 * @param {string} [props.subHeaderText] - Optional card subtitle text.
 * @param {string} [props.bodyText] - Optional card body content.
 * @param {boolean} [props.draggable] - Enable swipe/drag interactions on touch devices.
 * @param {oneOf} [props.variant] - Rendering variation: 'stack' or 'continuous'.
 */
Carousel.propTypes = {
    /** Array of card objects used to render slides. */
    cards: PropTypes.arrayOf(PropTypes.shape({
        /** Zero-based index of this card within the source array. */
        index: PropTypes.number.isRequired,
        /** Index of the currently active card as passed to each card. */
        cardIndex: PropTypes.number.isRequired,
        /** Total number of cards in the carousel (used for z-index/stacking). */
        cardLength: PropTypes.number.isRequired,
        /** Optional href for this card. */
        link: PropTypes.string,
        /** Optional link target attribute (e.g., '_self', '_blank'). */
        linkTarget: PropTypes.string,
        /** Image URL for the slide (required). */
        image: PropTypes.string.isRequired,
        /** Alt text for the image (used for accessibility). */
        imageAlt: PropTypes.string,
        /** Image fitting mode: 'contain' | 'cover' | 'fill'. */
        imgFit: PropTypes.oneOf(['contain', 'cover', 'fill']),
        /** Title or header text shown on the card. */
        headerText: PropTypes.string,
        /** Optional subtitle text for the card. */
        subHeaderText: PropTypes.string,
        /** Optional body content for the card. */
        bodyText: PropTypes.string,
    })).isRequired,
    /** Enable swipe/drag interactions on touch and pointer devices. */
    draggable: PropTypes.bool,
    imgFit: PropTypes.oneOf(['contain', 'cover', 'fill']),
    /** Rendering variation for the carousel. */
    variant: PropTypes.oneOf([...carouselVariants])
};
export function Carousel(props) {
    const variant = props.variant || 'stack';
    const debug = false;
    let timer = useRef(null);
    const [cardIndex, setcardIndex] = useState(0);
    function startTimer() {
        if (timer.current)
            clearTimeout(timer.current);
        timer.current = setTimeout(nextCard, 5000);
    }
    function stopTimer() {
        if (timer.current)
            clearTimeout(timer.current);
    }
    function previousCard() {
        if (debug)
            console.log("Going to Previous card : ", cardIndex, " => ", cardIndex - 1);
        if (cardIndex === 0) {
            setcardIndex(props.cards.length - 1);
        }
        else {
            setcardIndex(cardIndex - 1);
        }
        startTimer();
    }
    ;
    function nextCard() {
        if (debug)
            console.log("Going to Next card : ", cardIndex, " => ", cardIndex + 1);
        if (cardIndex === props.cards.length - 1) {
            setcardIndex(0);
        }
        else {
            setcardIndex(cardIndex + 1);
        }
        startTimer();
    }
    ;
    useEffect(() => {
        if (variant === 'stack' && typeof document !== 'undefined') {
            startTimer();
        }
    }, [cardIndex, variant]);
    useEffect(() => {
        setcardIndex(0);
    }, [props.cards]);
    /* ========== DRAGGABLE HANDLER ========== */
    if (variant === 'stack' && props.draggable && props.draggable === true) {
        if (debug)
            console.log('CarouselSimple: Dragging enabled');
        DragHandler({
            activeIndex: cardIndex,
            targetDiv: 'carousel-card-wrapper',
            nextImage: nextCard,
            previousImage: previousCard
        });
    }
    else {
        if (debug)
            console.log('CarouselSimple: Dragging disabled');
    }
    if (props.cards && props.cards.length > 0) {
        const cards = props.cards;
        const cardsToRender = variant === 'continuous' ? [...cards, ...cards] : cards;
        const trackStyle = {
            '--carousel-items': cards.length,
        };
        const renderedCards = cardsToRender.map((card, i) => (_jsx(CarouselCard, { index: i, cardIndex: cardIndex, cardLength: cardsToRender.length, link: card.link, linkTarget: card.linkTarget || '_self', image: card.image, imageAlt: card.imageAlt, imgFit: (props.imgFit || 'fill'), headerText: card.headerText, subHeaderText: card.subHeaderText, bodyText: card.bodyText, variant: variant }, `${variant}-${i}`)));
        return (_jsxs("div", { className: `carousel-container ${variant}`, suppressHydrationWarning: true, children: [_jsx("div", { className: "carousel-cards-container", children: variant === 'continuous' ? _jsx("div", { className: "carousel-track", style: trackStyle, children: renderedCards }) : renderedCards }), variant === 'stack' ? _jsxs("div", { className: "carousel-buttons", children: [_jsx(CarouselButton, { clickFunction: previousCard, glyph: '\u25C0' }), _jsx(CarouselButton, { clickFunction: stopTimer, glyph: '\u23F8' }), _jsx(CarouselButton, { clickFunction: nextCard, glyph: '\u25B6' })] }) : null] }));
    }
    else {
        return (_jsx("div", { className: 'section-container', children: _jsx("div", { className: "carousel-container", suppressHydrationWarning: true, children: _jsx(CarouselLoading, {}) }) }));
    }
}
/* ========== CAROUSEL CARD ========== */
/**
 * CarouselCard — renders an individual card within the Carousel, including image, text, and optional link.
 *
 * @param {number} [props.index] - Zero-based index of this card within the carousel.
 * @param {number} [props.cardIndex] - Current active index of the carousel (used for positioning).
 * @param {number} [props.cardLength] - Total number of cards in the carousel (used for z-index).
 * @param {string} [props.link] - Optional href for the card; if provided, wraps content in a link.
 * @param {string} [props.linkTarget] - Target attribute for the link (e.g., '_self', '_blank').
 * @param {string} [props.image] - Image URL for the card.
 * @param {string} [props.imageAlt] - Alt text for the image (accessibility).
 * @param {oneOf} [props.imgFit] - How to fit images: 'contain' | 'cover' | 'fill'.
 * @param {string} [props.headerText] - Optional header/title text for the card.
 * @param {string} [props.subHeaderText] - Optional subtitle text for the card.
 * @param {string} [props.bodyText] - Optional body content for the card.
 * @param {oneOf} [props.variant] - Rendering variation for the card.
 */
CarouselCard.propTypes = {
    /** Zero-based index of this card within the carousel. */
    index: PropTypes.number.isRequired,
    cardIndex: PropTypes.number.isRequired,
    cardLength: PropTypes.number.isRequired,
    link: PropTypes.string,
    linkTarget: PropTypes.string,
    image: PropTypes.string.isRequired,
    imageAlt: PropTypes.string,
    imgFit: PropTypes.oneOf(['contain', 'cover', 'fill']),
    headerText: PropTypes.string,
    subHeaderText: PropTypes.string,
    bodyText: PropTypes.string,
    /** Rendering variation for the card. */
    variant: PropTypes.oneOf([...carouselVariants]),
};
export function CarouselCard(props) {
    const styles = {};
    if (props.variant !== 'continuous') {
        styles.zIndex = props.cardLength - props.index;
        styles.transition = 'all 1.0s ease 0.1s';
        if (props.index > props.cardIndex) {
            styles.transform = 'translateX(100%)';
        }
        else if (props.index === props.cardIndex) {
            styles.transform = 'translateX(0%)';
        }
        else if (props.index < props.cardIndex) {
            styles.transform = 'translateX(-100%)';
        }
    }
    const imgFit = props.imgFit ? "img" + capitalize(props.imgFit) : 'imgFill';
    const config = usePixelatedConfig();
    const cardBody = (_jsxs("div", { draggable: 'false', children: [(props.link) ? _jsx("div", { draggable: 'false', className: "carousel-card-link" }) : null, (props.image) ? _jsx("div", { draggable: 'false', className: "carousel-card-image", children: _jsx(SmartImage, { draggable: false, src: props.image, title: props.imageAlt ?? undefined, alt: props?.imageAlt || "", className: imgFit, aboveFold: props?.index === 0 ? true : undefined, cloudinaryEnv: config?.integrations?.cloudinary?.product_env ?? undefined, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl ?? undefined, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms ?? undefined }) }) : null, (props.headerText) ? _jsx("div", { draggable: 'false', className: "carousel-card-header", children: _jsx("h3", { draggable: 'false', children: props.headerText }) }) : null, (props.subHeaderText) ? _jsx("div", { draggable: 'false', className: "carousel-card-subheader", children: _jsx("h4", { draggable: 'false', children: props.subHeaderText }) }) : null, (props.bodyText) ? _jsx("div", { draggable: 'false', className: "carousel-card-body", children: props.bodyText }) : null] }));
    return (_jsx("div", { draggable: 'true', id: 'c-' + props.index, className: "carousel-card-wrapper", style: styles, children: _jsx("div", { draggable: 'false', className: "carousel-card", children: (props.link) ? _jsx("a", { draggable: 'false', href: props.link, target: props.linkTarget ?? undefined, children: cardBody }) : cardBody }) }));
}
/* ========== CAROUSEL  ARROW ========== */
/**
 * CarouselButton — small interactive control used to navigate carousel slides.
 *
 * @param {function} [props.clickFunction] - Click handler invoked when the control is pressed.
 * @param {string} [props.glyph] - Label or HTML entity used to visually render the button.
 */
CarouselButton.propTypes = {
    /** Click handler invoked when the control is clicked. */
    clickFunction: PropTypes.func.isRequired,
    /** Label or glyph to display inside the button. */
    glyph: PropTypes.string.isRequired
};
function CarouselButton(props) {
    return (_jsx("button", { className: `carousel-button text-outline`, onClick: props.clickFunction, children: props.glyph }));
}
/**
 * CarouselArrow — Render a directional navigation control used by the Carousel.
 *
 * Renders a button styled for left or right placement and calls the provided handler when clicked.
 *
 * @param {('left'|'right')} [props.direction] - Direction used to determine button styling/placement.
 * @param {function} [props.clickFunction] - Click handler invoked when the control is pressed.
 * @param {string} [props.glyph] - Glyph or label text displayed inside the button.
 */
CarouselArrow.propTypes = {
    /** Direction keyword used to choose button styling/placement ('left'|'right'). */
    direction: PropTypes.string.isRequired,
    /** Click handler invoked by the arrow control. */
    clickFunction: PropTypes.func.isRequired,
    /** Glyph or label text used for the arrow control. */
    glyph: PropTypes.string.isRequired
};
export function CarouselArrow(props) {
    const { direction, clickFunction, glyph } = props;
    return (_jsx("button", { className: `carousel-button${capitalize(direction)} text-outline`, onClick: clickFunction, children: glyph }));
}
/* ========== CAROUSEL LOADING ========== */
CarouselLoading.propTypes = {
    /** Optional message to display while loading. */
    message: PropTypes.string
};
export function CarouselLoading(props) {
    return (_jsx("div", { className: "carousel-loading horizontal-centered vertical-centered centered", children: _jsx("div", { children: props.message ?? 'Loading...' }) }));
}
